import { error } from "./utils/utils.js";
import { Keyword, NaturalKeywordId } from "./model/keyword.js";
import { Country } from "./model/country.js";
import { Platform } from "./model/platform.js";
import { User } from "./model/user.js";
import { KeywordList } from "./model/keyword_list.js";
import { KeywordListWithKeywords } from "./model/keyword_list_with_keywords.js";
import { App } from "./model/app.js";
import { Rank } from "./model/rank.js";
import { ApiKey } from "./model/api_key.js";

export interface JsonValue {
    [key: string]: any;
}

function apiBaseUrl() {
    if (typeof location === "undefined") return "http://localhost:3333/api/";
    return location.href.includes("localhost") || location.href.includes("192.168.1") ? `http://${location.hostname}:3333/api/` : "/api/";
}

export async function apiGet<T>(endpoint: string, headers: HeadersInit = {}, autoRedirect = true): Promise<T | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint, { credentials: "include", headers: headers });
        handleResult(result, autoRedirect);
        return (await result.json()) as T;
    } catch (e) {
        return error(`Request GET /api/${endpoint} failed`, e);
    }
}

export async function apiGetText(endpoint: string): Promise<string | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint);
        handleResult(result);
        return await result.text();
    } catch (e) {
        return error(`Request GET /api/${endpoint} failed`, e);
    }
}

export async function apiGetBlob(endpoint: string): Promise<Blob | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint);
        handleResult(result);
        return await result.blob();
    } catch (e) {
        return error(`Request GET /api/${endpoint} failed`, e);
    }
}

export async function apiGetArrayBuffer(endpoint: string): Promise<ArrayBuffer | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint);
        handleResult(result);
        return await result.arrayBuffer();
    } catch (e) {
        return error(`Request GET /api/${endpoint} failed`, e);
    }
}

export async function apiPost<T>(
    endpoint: string,
    params: URLSearchParams | FormData | JsonValue | Blob | ArrayBuffer,
    contentType?: string,
    additionalHeaders?: HeadersInit
) {
    let headers: HeadersInit = {};
    let body: string | FormData | Blob | ArrayBuffer;

    if (params instanceof URLSearchParams) {
        headers = { "Content-Type": "application/x-www-form-urlencoded" };
        body = params.toString();
    } else if (params instanceof FormData) {
        body = params;
    } else if (params instanceof Blob || params instanceof ArrayBuffer) {
        // Handle binary data
        if (contentType) {
            headers = { "Content-Type": contentType };
        } else if (params instanceof Blob) {
            headers = { "Content-Type": params.type || "application/octet-stream" };
        } else {
            headers = { "Content-Type": "application/octet-stream" };
        }
        body = params;
    } else {
        // Handle JSON data
        headers = { "Content-Type": "application/json" };
        body = JSON.stringify(params);
    }

    const allHeaders = { ...headers, ...additionalHeaders };

    try {
        const result = await fetch(apiBaseUrl() + endpoint, {
            method: "POST",
            headers: allHeaders,
            body: body,
            credentials: "include",
        });
        handleResult(result);
        return (await result.json()) as T;
    } catch (e) {
        return error(`Request POST /api/${endpoint} failed`, e);
    }
}

export async function apiPut<T>(
    endpoint: string,
    params: URLSearchParams | FormData | JsonValue | Blob | ArrayBuffer,
    contentType?: string,
    additionalHeaders?: HeadersInit
) {
    let headers: HeadersInit = {};
    let body: string | FormData | Blob | ArrayBuffer;

    if (params instanceof URLSearchParams) {
        headers = { "Content-Type": "application/x-www-form-urlencoded" };
        body = params.toString();
    } else if (params instanceof FormData) {
        body = params;
    } else if (params instanceof Blob || params instanceof ArrayBuffer) {
        // Handle binary data
        if (contentType) {
            headers = { "Content-Type": contentType };
        } else if (params instanceof Blob) {
            headers = { "Content-Type": params.type || "application/octet-stream" };
        } else {
            headers = { "Content-Type": "application/octet-stream" };
        }
        body = params;
    } else {
        // Handle JSON data
        headers = { "Content-Type": "application/json" };
        body = JSON.stringify(params);
    }

    const allHeaders = { ...headers, ...additionalHeaders };

    try {
        const result = await fetch(apiBaseUrl() + endpoint, {
            method: "PUT",
            headers: allHeaders,
            body: body,
            credentials: "include",
        });
        handleResult(result);
        return (await result.json()) as T;
    } catch (e) {
        return error(`Request PUT /api/${endpoint} failed`, e);
    }
}

export async function apiDelete<T>(endpoint: string): Promise<T | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint, {
            method: "DELETE",
            credentials: "include",
        });
        handleResult(result);
        return await result.json();
    } catch (e) {
        return error(`Request DELETE /api/${endpoint} failed`, e);
    }
}

function handleResult(result: Response, autoRedirect = true) {
    if (result.ok) return;
    if (result.status === 401) {
        document.cookie = "authTokenExpiration=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        window.location.href = "/";
    }
    if (result.status === 402 && autoRedirect) {
        window.location.href = "/pricing";
    }
    throw new Error();
}

export class Api {
    static async user(): Promise<User | Error> {
        return await apiGet<User>("user");
    }

    static async customerPortalUrl(): Promise<{ url: string } | Error> {
        return await apiGet<{ url: string }>("customer-portal-url");
    }

    static async logout(): Promise<void | Error> {
        return await apiPost<void>("logout", {});
    }

    static async keyword(term: string, country: Country, platform: Platform, allApps: boolean, history: boolean): Promise<Keyword | Error> {
        const encodedTerm = encodeURIComponent(term.trim());
        const endpoint = `keyword?term=${encodedTerm}&country=${country}&platform=${platform}&allApps=${allApps}&history=${history}`;
        return await apiGet<Keyword>(endpoint);
    }

    static async refreshKeyword(term: string, country: Country, platform: Platform): Promise<Keyword | Error> {
        return await apiPost<Keyword>("keyword/refresh", { term, country, platform });
    }

    static async addKeywords(terms: string[], country: Country, platform: Platform, listId?: string): Promise<void | Error> {
        return await apiPost<void>(`keyword`, { terms, country, platform, listId }, "application/json");
    }

    static async history(): Promise<{ keywords: Keyword[] } | Error> {
        return await apiGet<{ keywords: Keyword[] }>(`history`, {}, false);
    }

    static async historyDelete(naturalKeywordIdString: string): Promise<void | Error> {
        const encodedKeywordId = encodeURIComponent(naturalKeywordIdString);
        return await apiDelete(`history?keywordId=${encodedKeywordId}`);
    }

    static async historyDeleteAll(): Promise<void | Error> {
        return await apiDelete(`history`);
    }

    static async getApps(query: string, country: Country, platform: Platform): Promise<App[] | Error> {
        return await apiGet<App[]>(`apps?query=${query}&country=${country}&platform=${platform}`);
    }

    static async getApp(appId: string, country: Country, keywordId?: number): Promise<App | Error> {
        let endpoint: string;
        if (keywordId) {
            endpoint = `app/${appId}?country=${country}&keywordId=${keywordId}`;
        } else {
            endpoint = `app/${appId}?country=${country}`;
        }
        return await apiGet<App>(endpoint);
    }

    static async listGetAll(): Promise<KeywordList[] | Error> {
        return await apiGet<KeywordList[]>(`list`, {}, false);
    }

    static async listGetById(listId: string): Promise<KeywordListWithKeywords | Error> {
        return await apiGet<KeywordListWithKeywords>(`list/${listId}`);
    }

    static async listCreate(): Promise<KeywordListWithKeywords | Error> {
        return await apiPost<KeywordListWithKeywords>(`list`, {});
    }

    static async updateListName(listId: string, name?: string): Promise<KeywordListWithKeywords | Error> {
        return await apiPut<KeywordListWithKeywords>(`list/${listId}`, { name });
    }

    static async updateListKeywordIds(listId: string, keywordIds?: number[]): Promise<KeywordListWithKeywords | Error> {
        return await apiPut<KeywordListWithKeywords>(`list/${listId}`, { keywordIds });
    }

    static async updateListApp(listId: string, appId: string, appCountry: Country, appPlatform: Platform): Promise<KeywordListWithKeywords | Error> {
        return await apiPut<KeywordListWithKeywords>(`list/${listId}`, { appId, appCountry, appPlatform });
    }

    static async deleteListApp(listId: string): Promise<KeywordListWithKeywords | Error> {
        return await apiDelete<KeywordListWithKeywords>(`list/${listId}/app`);
    }

    static async listDelete(listId: string): Promise<void | Error> {
        return await apiDelete<void>(`list/${listId}`);
    }

    static async newsletterSubscribe(email: string): Promise<{ success: boolean; message: string } | Error> {
        return await apiPost<{ success: boolean; message: string }>("newsletter-subscribe", { email: email });
    }

    static async magicLink(email: string): Promise<{ message: string } | Error> {
        return await apiPost<{ message: string }>("magic-link", { email: email });
    }

    static async apiKeysGetAll(): Promise<Omit<ApiKey, "keyHash">[] | Error> {
        return await apiGet<Omit<ApiKey, "keyHash">[]>("api-keys");
    }

    static async apiKeyCreate(name: string): Promise<{ id: string; name: string; key: string } | Error> {
        return await apiPost<{ id: string; name: string; key: string }>("api-keys", { name });
    }

    static async apiKeyDelete(id: string): Promise<{ message: string } | Error> {
        return await apiDelete<{ message: string }>(`api-keys/${id}`);
    }
}
